import axios, { AxiosInstance } from 'axios';
import { getTokenHeaderFromMsal } from '../helper/config';
import { msalInstance } from '../main';
import { createStore } from 'jotai';
import { globalAtom, globalState } from '../store/atom';

const store = createStore();
const axiosApiInstance: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_DEV,
});

axiosApiInstance.interceptors.request.use(
  async (config: any) => {
    try {
      const token = await getTokenHeaderFromMsal();
      config.headers['Authorization'] = `Bearer ${token}`;
      return config;
    } catch (error) {
      console.error('Failed to get token:', error);
      return Promise.reject(error);
    }
  },
  (error) => Promise.reject(error)
);

// Re-entrancy guard for sign-out. A single endpoint that keeps returning 401
// (e.g. a wrong/placeholder function key) must not spin logoutRedirect() in an
// infinite loop: sign out -> redirect -> land back on the page -> 401 again ->
// sign out ... We therefore trigger a sign-out at most once per window. The
// timestamp lives in localStorage so it survives the logout redirect.
const LOGOUT_GUARD_KEY = 'auth:lastLogoutAt';
const LOGOUT_GUARD_WINDOW_MS = 30000;

const recentlyLoggedOut = (): boolean => {
  try {
    const last = Number(localStorage.getItem(LOGOUT_GUARD_KEY) || 0);
    return last > 0 && Date.now() - last < LOGOUT_GUARD_WINDOW_MS;
  } catch {
    return false;
  }
};

// 401 -> the session is genuinely unauthenticated, so sign out (guarded against
// looping). 403 / 404 / everything else are ordinary API errors: reject and let
// the calling code handle them. We deliberately do NOT wipe browser storage or
// force a reload here — a single failing endpoint should never take down the
// whole session.
axiosApiInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !recentlyLoggedOut()) {
      try {
        localStorage.setItem(LOGOUT_GUARD_KEY, String(Date.now()));
      } catch {
        /* ignore storage write errors */
      }
      store.set(globalAtom, () => globalState);
      msalInstance.logoutRedirect();
    }
    return Promise.reject(error);
  }
);

export default axiosApiInstance;
